import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { sortMixes } from "@/lib/mixSort";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, MapPin, DollarSign, Link as LinkIcon, QrCode, Users, UserCircle2, Mail, CheckCircle2, Sparkles, PartyPopper, FileSpreadsheet } from "lucide-react";
import CsvImportDialog from "@/components/CsvImportDialog";
import { toast } from "sonner";
import { mixColor } from "@/lib/mixColor";
import CustomerTrackQR from "@/components/CustomerTrackQR";
import PortalUsersPanel from "@/components/PortalUsersPanel";
import BulkCardIdsDialog from "@/components/BulkCardIdsDialog";
import BulkClassifyDialog from "@/components/BulkClassifyDialog";
import { formatLocalDateTime } from "@/lib/dates";

const empty = {
  name: "", abn: "", phone: "", email: "", billing_address: "",
  customer_type: "account",
  pricing_class: "",   // v3.15 — "" | "A" | "B" | "C" | "CASH" | "CONTRACT"
  invoicing_mode: "daily_summary",   // v3.7 — "daily_summary" | "split_by_docket"
  fuel_surcharge_exempt: false,      // v3.10 — opt individual account customers out
  auto_print_docket: true,
  default_po_number: "",
  price_per_m3: 220, credit_terms_days: 30, credit_limit: 50000, notes: "",
  sites: [], contacts: [], mix_prices: [],
};

const PRICING_CLASSES = [
  { key: "",         label: "UNCLASSIFIED", hint: "No class · fallback pricing" },
  { key: "A",        label: "CLASS A",       hint: "Premium tier · account" },
  { key: "B",        label: "CLASS B",       hint: "Standard tier · account" },
  { key: "C",        label: "CLASS C",       hint: "Discount tier · account" },
  { key: "CASH",     label: "CASH / EFT",    hint: "Walk-in · $ printed on docket · same rate for Cash & EFT" },
  { key: "CONTRACT", label: "CONTRACT",      hint: "Negotiated · account" },
];

export default function Customers() {
  const [list, setList] = useState([]);
  const [mixes, setMixes] = useState([]);
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [qrCustomer, setQrCustomer] = useState(null);
  const [siteForm, setSiteForm] = useState({ name: "", address: "", lat: null, lng: null });
  const [tab, setTab] = useState("directory");   // "directory" | "portal"
  const [pendingCount, setPendingCount] = useState(0);
  const [inviteTarget, setInviteTarget] = useState(null);  // { customer, recipient_email, message, sending }
  const [bulkCidOpen, setBulkCidOpen] = useState(false);
  const [bulkClassifyOpen, setBulkClassifyOpen] = useState(false);
  // v3.16 — filter customers by pricing class ("" = show all, "UNCL" = unclassified only)
  const [classFilter, setClassFilter] = useState("all");

  const load = async () => {
    const [cRes, mRes] = await Promise.all([api.get("/customers"), api.get("/mixes")]);
    setList(cRes.data);
    setMixes(mRes.data);
  };
  useEffect(() => { load(); }, []);

  // Poll pending-signup count so the tab shows a live badge
  useEffect(() => {
    let cancelled = false;
    const fetchPending = async () => {
      try {
        const r = await api.get("/portal-users?status=pending");
        if (!cancelled) setPendingCount(r.data.length);
      } catch (_) {}
    };
    fetchPending();
    const t = setInterval(fetchPending, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...empty, ...c, mix_prices: c.mix_prices || [] }); setOpen(true); };

  const priceForMix = (form_, mix) => {
    const row = (form_.mix_prices || []).find((r) => r.mix_id === mix.id);
    return row ? row.price_per_m3 : "";
  };
  const setMixPrice = (mix, val) => {
    const num = val === "" ? null : parseFloat(val);
    const others = (form.mix_prices || []).filter((r) => r.mix_id !== mix.id);
    if (num === null || isNaN(num)) {
      setForm({ ...form, mix_prices: others });
    } else {
      setForm({ ...form, mix_prices: [...others, { mix_id: mix.id, price_per_m3: num }] });
    }
  };
  const bulkFillWithDefault = () => {
    const rows = mixes.map((m) => ({ mix_id: m.id, price_per_m3: form.price_per_m3 }));
    setForm({ ...form, mix_prices: rows });
    toast.success(`Set ${rows.length} mix prices to $${Number(form.price_per_m3).toFixed(2)}`);
  };
  const clearAllMixPrices = () => {
    setForm({ ...form, mix_prices: [] });
    toast.success("Cleared per-mix prices — defaults apply");
  };

  const save = async () => {
    try {
      // v3.16 · Sanitise numeric fields — an empty or NaN price_per_m3 /
      // credit_terms_days would serialise to `null` and 422 the API, which
      // used to leave the dialog wedged behind the backdrop. Zero is a
      // legitimate value (customer relies purely on class pricing).
      const num = (v, fallback = 0) => {
        const n = typeof v === "number" ? v : parseFloat(v);
        return Number.isFinite(n) ? n : fallback;
      };
      const payload = {
        ...form,
        price_per_m3: num(form.price_per_m3, 0),
        credit_terms_days: Math.max(0, Math.trunc(num(form.credit_terms_days, 30))),
        credit_limit: num(form.credit_limit, 0),
      };
      if (editing) await api.put(`/customers/${editing.id}`, payload);
      else await api.post("/customers", payload);
      toast.success("Saved");
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete customer?")) return;
    await api.delete(`/customers/${id}`);
    toast.success("Deleted"); load();
  };
  const addSite = () => {
    if (!siteForm.name) return;
    setForm({ ...form, sites: [...(form.sites || []), { ...siteForm, id: crypto.randomUUID() }] });
    setSiteForm({ name: "", address: "", lat: null, lng: null });
  };
  const removeSite = (id) => setForm({ ...form, sites: form.sites.filter((s) => s.id !== id) });

  const openInvite = (c) => {
    setInviteTarget({
      customer: c,
      recipient_email: c.email || "",
      message: "",
      sending: false,
    });
  };
  const sendInvite = async () => {
    if (!inviteTarget) return;
    const { customer, recipient_email, message } = inviteTarget;
    if (!recipient_email || !recipient_email.includes("@")) {
      toast.error("Enter a valid recipient email");
      return;
    }
    setInviteTarget({ ...inviteTarget, sending: true });
    try {
      const r = await api.post(`/customers/${customer.id}/invite-portal`, {
        recipient_email,
        message: message || null,
      });
      if (r.data?.sandbox) {
        toast.success(`Invite queued (sandbox → ${r.data.sent_to}). Intended: ${r.data.intended_to}`, { duration: 7000 });
      } else {
        toast.success(`Portal invite sent to ${r.data.sent_to}`);
      }
      setInviteTarget(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send invite");
      setInviteTarget((t) => t ? { ...t, sending: false } : t);
    }
  };

  // Compact mix-price summary for the customer list
  const mixPriceSummary = (c) => {
    const rows = c.mix_prices || [];
    if (rows.length === 0) return null;
    const prices = rows.map((r) => r.price_per_m3).filter((n) => typeof n === "number");
    if (prices.length === 0) return null;
    const min = Math.min(...prices);
    const max = Math.max(...prices);
    return { count: prices.length, min, max };
  };

  // v3.16 — Class-filter chips. "UNCL" narrows to customers with no pricing_class.
  const classCounts = list.reduce((acc, c) => {
    const key = (c.pricing_class || "").toUpperCase();
    const bucket = key || "UNCL";
    acc[bucket] = (acc[bucket] || 0) + 1;
    acc.all = (acc.all || 0) + 1;
    return acc;
  }, {});
  const filteredList = list.filter((c) => {
    if (classFilter === "all") return true;
    const key = (c.pricing_class || "").toUpperCase();
    if (classFilter === "UNCL") return !key;
    return key === classFilter;
  });

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="customers-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-tag">DIRECTORY</div>
          <h1 className="heading text-3xl font-black uppercase">Customers</h1>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex border border-industrial">
            <button
              data-testid="tab-directory"
              onClick={() => setTab("directory")}
              className={`mono text-[10px] uppercase tracking-widest px-3 py-2 inline-flex items-center gap-1.5 transition-colors ${tab === "directory" ? "bg-primary-orange text-black font-bold" : "text-muted-industrial hover:text-ink"}`}
            >
              <UserCircle2 className="w-3.5 h-3.5" />Directory
              <span className="text-[10px]">· {list.length}</span>
            </button>
            <button
              data-testid="tab-portal"
              onClick={() => setTab("portal")}
              className={`mono text-[10px] uppercase tracking-widest px-3 py-2 inline-flex items-center gap-1.5 transition-colors border-l border-industrial ${tab === "portal" ? "bg-primary-orange text-black font-bold" : "text-muted-industrial hover:text-ink"}`}
            >
              <Users className="w-3.5 h-3.5" />Portal Users
              {pendingCount > 0 && (
                <span className="ml-1 mono text-[10px] px-1.5 py-0.5 bg-primary-orange text-black font-bold" data-testid="portal-pending-badge">
                  {pendingCount}
                </span>
              )}
            </button>
          </div>
          {tab === "directory" && (
            <div className="flex items-center gap-2">
              <Button data-testid="bulk-classify-btn" onClick={() => setBulkClassifyOpen(true)} variant="outline" className="rounded-none border-primary-orange text-primary-orange hover:bg-primary-orange hover:text-black uppercase tracking-widest text-xs font-bold">
                <UserCircle2 className="w-4 h-4 mr-1" />Bulk Classify
              </Button>
              <Button data-testid="bulk-card-ids-btn" onClick={() => setBulkCidOpen(true)} variant="outline" className="rounded-none border-primary-orange text-primary-orange hover:bg-primary-orange hover:text-black uppercase tracking-widest text-xs font-bold">
                <Sparkles className="w-4 h-4 mr-1" />Bulk Card IDs
              </Button>
              <Button data-testid="import-customers-btn" variant="outline" onClick={() => setImportOpen(true)} className="rounded-none border-industrial uppercase tracking-widest text-xs font-bold"><FileSpreadsheet className="w-4 h-4 mr-1 text-primary-orange" />Import</Button>
              <Button data-testid="new-customer-btn" onClick={openNew} className="rounded-none bg-primary-orange hover:bg-[rgb(var(--primary-hover))] uppercase tracking-widest text-xs font-bold">
                <Plus className="w-4 h-4 mr-1" />New Customer
              </Button>
            </div>
          )}
        </div>
      </div>
      <BulkCardIdsDialog open={bulkCidOpen} onOpenChange={setBulkCidOpen} onDone={load} />
      <BulkClassifyDialog
        open={bulkClassifyOpen}
        onOpenChange={setBulkClassifyOpen}
        customers={list}
        onDone={load}
      />

      {tab === "directory" && (
        <div className="flex items-center flex-wrap gap-1.5" data-testid="class-filter-chips">
          {[
            { key: "all",      label: "ALL",         style: "text-ink" },
            { key: "UNCL",     label: "UNCL.",       style: "text-red-500" },
            { key: "A",        label: "CLASS A",     style: "text-emerald-500" },
            { key: "B",        label: "CLASS B",     style: "text-sky-500" },
            { key: "C",        label: "CLASS C",     style: "text-orange-500" },
            { key: "CASH",     label: "CASH / EFT",  style: "text-yellow-500" },
            { key: "CONTRACT", label: "CONTRACT",    style: "text-blue-500" },
          ].map((chip) => {
            const active = classFilter === chip.key;
            const count = classCounts[chip.key] || 0;
            return (
              <button
                key={chip.key}
                data-testid={`class-chip-${chip.key}`}
                onClick={() => setClassFilter(chip.key)}
                className={`mono text-[10px] uppercase tracking-widest px-2 py-1 border transition-colors ${active ? "border-primary-orange bg-primary-orange text-black font-bold" : `border-industrial ${chip.style} hover:border-primary-orange/60`}`}
              >
                {chip.label} <span className="opacity-70">· {count}</span>
              </button>
            );
          })}
        </div>
      )}

      {tab === "portal" ? (
        <PortalUsersPanel customers={list} />
      ) : (
      <div className="card-industrial overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-industrial">
            <tr className="text-left">
              <th className="p-3 label-tag">Customer</th>
              <th className="p-3 label-tag">Phone</th>
              <th className="p-3 label-tag">Sites</th>
              <th className="p-3 label-tag text-right">Default $/m³</th>
              <th className="p-3 label-tag">Mix Pricing</th>
              <th className="p-3 label-tag text-right">Terms</th>
              <th className="p-3 label-tag text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredList.map((c) => {
              const sum = mixPriceSummary(c);
              return (
                <tr key={c.id} data-testid={`customer-row-${c.id}`}
                    className="border-b border-industrial hover:bg-surface-2 cursor-pointer"
                    onClick={() => openEdit(c)}>
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      <div className="font-semibold">{c.name}</div>
                      {c.pricing_class ? (
                        <span
                          className={`mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 border font-bold ${
                            c.pricing_class === "CASH" ? "border-yellow-500 text-yellow-500 bg-yellow-500/10" :
                            c.pricing_class === "CONTRACT" ? "border-blue-500 text-blue-500 bg-blue-500/10" :
                            c.pricing_class === "A" ? "border-emerald-500 text-emerald-500 bg-emerald-500/10" :
                            c.pricing_class === "B" ? "border-sky-500 text-sky-500 bg-sky-500/10" :
                            c.pricing_class === "C" ? "border-orange-500 text-orange-500 bg-orange-500/10" :
                            "border-industrial text-muted-industrial"
                          }`}
                          data-testid={`class-badge-${c.id}`}
                        >
                          {c.pricing_class === "CASH" ? "CASH / EFT" : c.pricing_class}
                        </span>
                      ) : (
                        <span className="mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 border border-red-500/60 text-red-500 bg-red-500/10 font-bold"
                              title="No pricing class assigned — using fallback rates">
                          UNCL.
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-muted-industrial mono">{c.abn}</div>
                  </td>
                  <td className="p-3 mono text-xs">{c.phone}</td>
                  <td className="p-3 mono text-xs">{c.sites?.length || 0}</td>
                  <td className="p-3 mono text-right">${c.price_per_m3?.toFixed(2)}</td>
                  <td className="p-3">
                    {sum ? (
                      <div className="flex items-center gap-1.5">
                        <span className="mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 border border-primary-orange text-primary-orange">
                          {sum.count} mix{sum.count > 1 ? "es" : ""}
                        </span>
                        <span className="mono text-xs text-muted-industrial">
                          ${sum.min.toFixed(0)}
                          {sum.min !== sum.max && <> – ${sum.max.toFixed(0)}</>}
                        </span>
                      </div>
                    ) : (
                      <span className="mono text-[10px] uppercase tracking-widest text-muted-industrial">Uses default</span>
                    )}
                  </td>
                  <td className="p-3 mono text-right">{c.credit_terms_days}d</td>
                  <td className="p-3 text-right">
                    <div className="inline-flex items-center gap-1 justify-end">
                      <button data-testid={`welcome-email-${c.id}`}
                              onClick={async (e) => {
                                e.stopPropagation();
                                if (!c.email) { toast.error("Add an email on this customer first"); return; }
                                const already = !!c.welcome_email_sent_at;
                                const ok = window.confirm(
                                  already
                                    ? `Re-send the Welcome to the New Gadtech Online Portal email to ${c.name} at ${c.email}?\n\nAlready sent ${formatLocalDateTime(c.welcome_email_sent_at)}.`
                                    : `Send the Welcome to the New Gadtech Online Portal email to ${c.name} at ${c.email}?`
                                );
                                if (!ok) return;
                                try {
                                  const r = await api.post(`/customers/${c.id}/welcome-email`);
                                  toast.success(
                                    r.data.sandbox
                                      ? `Sandbox: welcome email routed to ${r.data.delivered_to} (verify gadtech.com.au in Resend to send direct)`
                                      : `Welcome email sent to ${r.data.original_recipient}`,
                                    { duration: 6000 }
                                  );
                                  await load();
                                } catch (err) {
                                  toast.error(err?.response?.data?.detail || "Failed to send welcome email");
                                }
                              }}
                              title={c.welcome_email_sent_at
                                ? `Welcome email last sent ${formatLocalDateTime(c.welcome_email_sent_at)} to ${c.welcome_email_sent_to || c.email} — click to resend`
                                : "Send the Gadtech Online Portal welcome email"}
                              className={`hover:text-primary-orange ${c.welcome_email_sent_at ? "text-emerald-400" : "text-muted-industrial"}`}>
                        <PartyPopper className="w-4 h-4" />
                      </button>
                      <button data-testid={`invite-portal-${c.id}`}
                              onClick={(e) => { e.stopPropagation(); openInvite(c); }}
                              title={c.portal_invited_at ? `Portal invite last sent ${formatLocalDateTime(c.portal_invited_at)} to ${c.portal_invited_to || c.email}` : "Email portal invite"}
                              className={`hover:text-primary-orange ${c.portal_invited_at ? "text-primary-orange" : "text-muted-industrial"}`}>
                        <Mail className="w-4 h-4" />
                      </button>
                      <button data-testid={`qr-link-${c.id}`}
                              onClick={(e) => { e.stopPropagation(); setQrCustomer(c); }}
                              title="Show QR code for this customer's tracking link"
                              className="text-muted-industrial hover:text-primary-orange">
                        <QrCode className="w-4 h-4" />
                      </button>
                      <button data-testid={`track-link-${c.id}`}
                              onClick={async (e) => {
                                e.stopPropagation();
                                try {
                                  const r = await api.post(`/customers/${c.id}/ensure-track-token`);
                                  const url = `${window.location.origin}/track/${r.data.track_token}`;
                                  await navigator.clipboard.writeText(url);
                                  toast.success("Tracking link copied to clipboard");
                                } catch (err) {
                                  toast.error("Could not create tracking link");
                                }
                              }}
                              title="Copy live tracking link for this customer"
                              className="text-muted-industrial hover:text-primary-orange">
                        <LinkIcon className="w-4 h-4" />
                      </button>
                      <button data-testid={`del-customer-${c.id}`}
                              onClick={(e) => { e.stopPropagation(); del(c.id); }}
                              className="text-muted-industrial hover:text-primary-orange">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      )}

      <CsvImportDialog entity="customers" open={importOpen} onOpenChange={setImportOpen} onDone={load} />
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none bg-surface border-industrial max-w-3xl" data-testid="customer-dialog">
          <DialogHeader><DialogTitle className="heading uppercase">{editing ? "Edit" : "New"} Customer</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 max-h-[70vh] overflow-y-auto pr-2">
            <div><Label className="label-tag">Name</Label>
              <Input data-testid="cust-name" value={form.name}
                     onChange={(e) => setForm({ ...form, name: e.target.value })}
                     className="rounded-none bg-transparent border-industrial mono" /></div>
            <div><Label className="label-tag">ABN</Label>
              <Input value={form.abn}
                     onChange={(e) => setForm({ ...form, abn: e.target.value })}
                     className="rounded-none bg-transparent border-industrial mono" /></div>
            <div><Label className="label-tag">Phone</Label>
              <Input value={form.phone}
                     onChange={(e) => setForm({ ...form, phone: e.target.value })}
                     className="rounded-none bg-transparent border-industrial mono" /></div>
            <div><Label className="label-tag">Email</Label>
              <Input value={form.email}
                     onChange={(e) => setForm({ ...form, email: e.target.value })}
                     className="rounded-none bg-transparent border-industrial mono" /></div>
            <div className="col-span-2"><Label className="label-tag">Billing Address</Label>
              <Input value={form.billing_address}
                     onChange={(e) => setForm({ ...form, billing_address: e.target.value })}
                     className="rounded-none bg-transparent border-industrial mono" /></div>
            <div><Label className="label-tag">Default Price / m³</Label>
              <Input type="number" step="0.5"
                     value={Number.isFinite(form.price_per_m3) ? form.price_per_m3 : ""}
                     onChange={(e) => {
                       const v = e.target.value;
                       const n = parseFloat(v);
                       setForm({ ...form, price_per_m3: v === "" ? 0 : (Number.isFinite(n) ? n : 0) });
                     }}
                     className="rounded-none bg-transparent border-industrial mono" /></div>
            <div><Label className="label-tag">Credit Terms (days)</Label>
              <Input type="number"
                     value={Number.isFinite(form.credit_terms_days) ? form.credit_terms_days : ""}
                     onChange={(e) => {
                       const v = e.target.value;
                       const n = parseInt(v, 10);
                       setForm({ ...form, credit_terms_days: v === "" ? 0 : (Number.isFinite(n) ? n : 0) });
                     }}
                     className="rounded-none bg-transparent border-industrial mono" /></div>
            <div><Label className="label-tag">Account Code</Label>
              <Input value={form.account_code}
                     onChange={(e) => setForm({ ...form, account_code: e.target.value })}
                     placeholder="e.g. SBC-001"
                     className="rounded-none bg-transparent border-industrial mono" /></div>
            <div><Label className="label-tag">MYOB / Xero Export Code</Label>
              <Input
                data-testid="cust-gl-code"
                value={form.gl_code}
                onChange={(e) => setForm({ ...form, gl_code: e.target.value })}
                placeholder="Customer card ID or GL code"
                className="rounded-none bg-transparent border-industrial mono"
              /></div>
            <div className="col-span-2"><Label className="label-tag">Standing PO Number</Label>
              <Input
                data-testid="cust-default-po"
                value={form.default_po_number || ""}
                onChange={(e) => setForm({ ...form, default_po_number: e.target.value })}
                placeholder="Auto-fills on every new order — e.g. PO-4471"
                className="rounded-none bg-transparent border-industrial mono uppercase"
              />
              <div className="mono text-[10px] uppercase tracking-widest text-muted-industrial mt-1">
                For a builder running one PO across a whole subdivision. Still overridable per order.
              </div>
            </div>
            <div className="col-span-2">
              <Label className="label-tag">Pricing Class</Label>
              <div className="grid grid-cols-3 gap-2 mt-1">
                {PRICING_CLASSES.map(({ key, label, hint }) => (
                  <button
                    key={key || "unc"}
                    type="button"
                    data-testid={`cust-class-${key || "unclassified"}`}
                    onClick={() => setForm({
                      ...form,
                      pricing_class: key,
                      customer_type: key === "CASH" ? "cash" : (key && key !== "" ? "account" : form.customer_type),
                    })}
                    className={`text-left px-3 py-2 border transition-colors ${form.pricing_class === key ? "border-primary-orange bg-primary-orange/10 text-primary-orange" : "border-industrial text-muted-industrial hover:text-ink"}`}
                  >
                    <div className="mono uppercase text-xs tracking-widest font-bold">{label}</div>
                    <div className="text-[10px] uppercase tracking-wider opacity-70 mt-0.5">{hint}</div>
                  </button>
                ))}
              </div>
              <div className="mt-2 text-[10px] uppercase tracking-widest text-muted-industrial">
                Docket type: <span className="text-ink font-bold">{form.customer_type === "cash" ? "CASH · $ printed" : "ACCOUNT · POD only"}</span>
                <span className="ml-3 opacity-70">Auto-synced from class</span>
              </div>
            </div>

            <div className="col-span-2">
              <Label className="label-tag">Invoicing Mode</Label>
              <div className="flex gap-2 mt-1">
                <button
                  type="button"
                  data-testid="cust-inv-mode-daily"
                  onClick={() => setForm({ ...form, invoicing_mode: "daily_summary" })}
                  className={`flex-1 py-2 mono uppercase text-xs tracking-widest border transition-colors ${(form.invoicing_mode || "daily_summary") === "daily_summary" ? "border-primary-orange bg-primary-orange/10 text-primary-orange font-bold" : "border-industrial text-muted-industrial hover:text-ink"}`}
                >
                  Daily Summary
                </button>
                <button
                  type="button"
                  data-testid="cust-inv-mode-split"
                  onClick={() => setForm({ ...form, invoicing_mode: "split_by_docket" })}
                  className={`flex-1 py-2 mono uppercase text-xs tracking-widest border transition-colors ${form.invoicing_mode === "split_by_docket" ? "border-primary-orange bg-primary-orange/10 text-primary-orange font-bold" : "border-industrial text-muted-industrial hover:text-ink"}`}
                >
                  Split by Docket
                </button>
              </div>
              <div className="mono text-[10px] uppercase tracking-widest text-muted-industrial mt-1">
                Daily Summary still auto-splits when jobs have different sites or PO numbers.
              </div>
            </div>

            {/* Auto docket print — per customer */}
            <div className="col-span-2 border border-industrial p-3 flex items-start gap-3" data-testid="cust-auto-print-row">
              <input
                type="checkbox"
                id="auto-print-cb"
                data-testid="cust-auto-print-cb"
                checked={form.auto_print_docket !== false}
                onChange={(e) => setForm({ ...form, auto_print_docket: e.target.checked })}
                className="mt-1 h-4 w-4 accent-primary-orange cursor-pointer"
              />
              <label htmlFor="auto-print-cb" className="flex-1 cursor-pointer">
                <div className="mono uppercase text-xs tracking-widest font-bold flex items-center gap-1">
                  Auto-print docket on load completed
                  {form.auto_print_docket === false && <span className="mono text-[9px] px-1.5 py-0.5 border border-amber-500 text-amber-400">EMAIL ONLY</span>}
                </div>
                <div className="text-[10px] uppercase tracking-wider opacity-70 mt-0.5">
                  Prints a paper docket at the yard printer the moment each load finishes batching. Untick for customers who only want emailed dockets.
                </div>
              </label>
            </div>

            {/* v3.10 · Fuel surcharge exempt toggle — only meaningful for account customers */}
            {form.customer_type !== "cash" && (
              <div className="col-span-2 border border-industrial p-3 flex items-start gap-3" data-testid="cust-fuel-exempt-row">
                <input
                  type="checkbox"
                  id="fuel-exempt-cb"
                  data-testid="cust-fuel-exempt-cb"
                  checked={!!form.fuel_surcharge_exempt}
                  onChange={(e) => setForm({ ...form, fuel_surcharge_exempt: e.target.checked })}
                  className="mt-1 h-4 w-4 accent-primary-orange cursor-pointer"
                />
                <label htmlFor="fuel-exempt-cb" className="flex-1 cursor-pointer">
                  <div className="mono uppercase text-xs tracking-widest font-bold flex items-center gap-1">
                    Exempt from Fuel Surcharge
                    {form.fuel_surcharge_exempt && <span className="mono text-[9px] px-1.5 py-0.5 border border-emerald-500 text-emerald-400">EXEMPT</span>}
                  </div>
                  <div className="text-[10px] uppercase tracking-wider opacity-70 mt-0.5">
                    When enabled globally, every account invoice gets a fuel surcharge line based on today's diesel TGP. Tick this to exclude — typically used for CONTRACT customers with locked-in pricing.
                  </div>
                </label>
              </div>
            )}

            {/* ─── Per-Mix Pricing ─────────────────────────────────────── */}
            <div className="col-span-2 border-t border-industrial pt-3 mt-1">
              <div className="flex items-end justify-between mb-2">
                <div className="label-tag flex items-center gap-2">
                  <DollarSign className="w-3 h-3" />Per-Mix Pricing
                </div>
                <div className="flex gap-1.5">
                  <button type="button" data-testid="bulk-fill-prices" onClick={bulkFillWithDefault}
                          className="mono text-[10px] uppercase tracking-widest px-2 py-1 border border-industrial hover:border-primary-orange text-muted-industrial hover:text-primary-orange">
                    Fill w/ default
                  </button>
                  <button type="button" data-testid="clear-prices" onClick={clearAllMixPrices}
                          className="mono text-[10px] uppercase tracking-widest px-2 py-1 border border-industrial hover:border-red-700 text-muted-industrial hover:text-red-400">
                    Clear all
                  </button>
                </div>
              </div>
              <div className="text-[10px] text-muted-industrial mono uppercase tracking-widest mb-2">
                Leave blank to use default (${Number(form.price_per_m3 || 0).toFixed(2)}). Set a value to override this mix for this customer.
              </div>
              <div className="border border-industrial overflow-hidden">
                <div className="grid grid-cols-[80px_1fr_140px_100px] gap-0 text-[10px] mono uppercase tracking-widest bg-surface-2 border-b border-industrial">
                  <div className="p-2 text-muted-industrial">Code</div>
                  <div className="p-2 text-muted-industrial">Mix Name</div>
                  <div className="p-2 text-muted-industrial">Slump · Strength</div>
                  <div className="p-2 text-muted-industrial text-right">$ / m³</div>
                </div>
                {mixes.length === 0 && (
                  <div className="p-3 text-muted-industrial text-xs mono uppercase tracking-widest">No mixes defined yet</div>
                )}
                {sortMixes(mixes).map((m) => {
                  const mc = mixColor(m.code);
                  const cur = priceForMix(form, m);
                  const isSet = cur !== "" && cur !== null && cur !== undefined;
                  return (
                    <div key={m.id} className={`grid grid-cols-[80px_1fr_140px_100px] items-center gap-0 border-b border-industrial last:border-b-0 text-sm ${isSet ? "bg-primary-orange/5" : ""}`} data-testid={`mix-price-row-${m.code}`}>
                      <div className="p-2">
                        <span className="mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 border inline-block"
                              style={{ borderColor: mc.border, color: mc.hex, background: mc.bg }}>
                          {m.code}
                        </span>
                      </div>
                      <div className="p-2 text-sm truncate" title={m.name}>{m.name}</div>
                      <div className="p-2 mono text-[11px] text-muted-industrial">
                        {m.slump_mm ? `${m.slump_mm}mm` : "—"}
                        {m.strength_mpa ? ` · ${m.strength_mpa}MPa` : ""}
                      </div>
                      <div className="p-1.5">
                        <Input
                          type="number"
                          step="0.5"
                          value={cur}
                          onChange={(e) => setMixPrice(m, e.target.value)}
                          placeholder={Number(form.price_per_m3 || 0).toFixed(2)}
                          data-testid={`mix-price-input-${m.code}`}
                          className="rounded-none bg-transparent border-industrial mono h-8 text-right text-sm"
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* ─── Delivery Sites ─────────────────────────────────────── */}
            <div className="col-span-2 border-t border-industrial pt-3">
              <div className="label-tag mb-2 flex items-center gap-2"><MapPin className="w-3 h-3" />Delivery Sites</div>
              <div className="space-y-1.5">
                {form.sites?.map((s) => (
                  <div key={s.id} className="border border-industrial p-2 flex justify-between text-xs">
                    <div><span className="font-semibold">{s.name}</span> · <span className="mono text-muted-industrial">{s.address}</span></div>
                    <button onClick={() => removeSite(s.id)} className="text-muted-industrial hover:text-primary-orange"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
                <div className="flex gap-1.5 flex-wrap">
                  <Input placeholder="Site name" value={siteForm.name} onChange={(e) => setSiteForm({ ...siteForm, name: e.target.value })} className="rounded-none bg-transparent border-industrial mono text-xs flex-1 min-w-[120px]" />
                  <Input placeholder="Address" value={siteForm.address} onChange={(e) => setSiteForm({ ...siteForm, address: e.target.value })} className="rounded-none bg-transparent border-industrial mono text-xs flex-1 min-w-[120px]" />
                  <Input placeholder="Lat" type="number" step="0.0001" value={siteForm.lat || ""} onChange={(e) => setSiteForm({ ...siteForm, lat: parseFloat(e.target.value) })} className="rounded-none bg-transparent border-industrial mono text-xs w-24" data-testid="site-lat" />
                  <Input placeholder="Lng" type="number" step="0.0001" value={siteForm.lng || ""} onChange={(e) => setSiteForm({ ...siteForm, lng: parseFloat(e.target.value) })} className="rounded-none bg-transparent border-industrial mono text-xs w-24" data-testid="site-lng" />
                  <Button size="sm" onClick={addSite} className="rounded-none bg-surface-2 hover:bg-surface border border-industrial uppercase text-xs">Add</Button>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="save-customer-btn" onClick={save}
                    className="rounded-none bg-primary-orange hover:bg-[rgb(var(--primary-hover))] uppercase tracking-widest text-xs font-bold">
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* v3.1 · Tracking QR dialog */}
      <CustomerTrackQR open={!!qrCustomer} onOpenChange={(v) => { if (!v) setQrCustomer(null); }} customer={qrCustomer} />

      {/* v3.4 · Portal invite dialog */}
      <Dialog open={!!inviteTarget} onOpenChange={(v) => { if (!v && !inviteTarget?.sending) setInviteTarget(null); }}>
        <DialogContent className="rounded-none bg-surface border-industrial max-w-lg" data-testid="invite-portal-dialog">
          <DialogHeader>
            <DialogTitle className="heading uppercase flex items-center gap-2">
              <Mail className="w-4 h-4 text-primary-orange" />
              Invite to Customer Portal
            </DialogTitle>
          </DialogHeader>
          {inviteTarget && (
            <div className="space-y-4">
              <div className="border border-industrial p-3 mono text-xs space-y-1">
                <div className="label-tag">Customer</div>
                <div className="text-sm font-semibold">{inviteTarget.customer.name}</div>
                {inviteTarget.customer.account_code && (
                  <div className="text-muted-industrial">Account code · {inviteTarget.customer.account_code}</div>
                )}
                {inviteTarget.customer.portal_invited_at && (
                  <div className="text-primary-orange text-[10px] uppercase tracking-widest flex items-center gap-1 pt-1">
                    <CheckCircle2 className="w-3 h-3" />
                    Previously invited {formatLocalDateTime(inviteTarget.customer.portal_invited_at)}
                    {inviteTarget.customer.portal_invited_to && <> → {inviteTarget.customer.portal_invited_to}</>}
                  </div>
                )}
              </div>
              <div>
                <Label className="label-tag">Recipient email</Label>
                <Input
                  data-testid="invite-email-input"
                  type="email"
                  value={inviteTarget.recipient_email}
                  onChange={(e) => setInviteTarget({ ...inviteTarget, recipient_email: e.target.value })}
                  placeholder="site.manager@example.com"
                  className="rounded-none bg-transparent border-industrial mono mt-1"
                />
              </div>
              <div>
                <Label className="label-tag">Personal note (optional)</Label>
                <textarea
                  data-testid="invite-message-input"
                  value={inviteTarget.message}
                  onChange={(e) => setInviteTarget({ ...inviteTarget, message: e.target.value })}
                  rows={3}
                  maxLength={600}
                  placeholder="e.g. Hi John — here's the portal we discussed. You'll be able to book concrete and download dockets anytime."
                  className="w-full mt-1 rounded-none bg-transparent border border-industrial mono text-sm p-2 resize-none"
                />
                <div className="text-[10px] mono uppercase tracking-widest text-muted-industrial mt-1">
                  Shown inside the invite email above the "Create your account" button.
                </div>
              </div>
              <div className="text-[10px] mono uppercase tracking-widest text-muted-industrial border-t border-industrial pt-2">
                The email includes a one-click link to <span className="text-primary-orange">/portal/register</span> with
                their email {inviteTarget.customer.account_code ? "and account code " : ""}pre-filled — signup will auto-link back to this customer.
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              data-testid="invite-send-btn"
              onClick={sendInvite}
              disabled={inviteTarget?.sending}
              className="rounded-none bg-primary-orange hover:bg-[rgb(var(--primary-hover))] uppercase tracking-widest text-xs font-bold disabled:opacity-40"
            >
              <Mail className="w-3.5 h-3.5 mr-1.5" />
              {inviteTarget?.sending ? "Sending…" : "Send Invite"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
